/**
 * What the dictionary's three maps — `spell_check.corrections`,
 * `spell_check.never_suggest` and `search.synonyms` — have in common (I-3024):
 * one level per language (`{ de: { <key>: <entry> } }`), the layers merge to
 * their UNION, an entry is written and removed one by one with a merge patch
 * (`null` removes it), and a tenant or company locks a whole top-level field.
 * Pure functions, no Vue — the config helpers of each field and the shared
 * tabs build on them.
 */
import type {
  AccountConfigLayer,
  AccountConfigTarget,
  EnforcedField,
} from "./accountConfig";
import {
  accountConfigLayerSource,
  accountConfigLayerView,
  type AccountConfigLayerRef,
} from "./layerView";
import { isJsonObject, type JsonObject } from "./mergePatch";
import {
  DICTIONARY_DOCUMENT_MAX_BYTES,
  DICTIONARY_LANGUAGES,
  dictionaryDocumentBytes,
  type DictionaryLanguage,
} from "../text/dictionaryFile";

/**
 * Where an entry of the effective dictionary comes from: the layer that is
 * written (`own`), a company, a tenant, or another layer before it (the
 * default profile under another profile).
 */
export type DictionaryEntrySource = "own" | "company" | "tenant" | "inherited";

/**
 * The dictionary is no part of a design (I-3024): master's dictionary
 * (`searchdictionary`) reads the tenant, company, account and profile layers
 * and ignores an `appearance` layer, so a word a design carries neither
 * suggests nor hides anything. The client agrees — every view and filter of
 * the dictionary works on the layers without the design layers.
 */
export function dictionaryLayers(
  layers: readonly AccountConfigLayer[],
): AccountConfigLayer[] {
  return layers.filter((layer) => layer.subjectType !== "appearance");
}

/** A layer past the end of the chain: the view of it is every real layer merged. */
const END_OF_CHAIN: AccountConfigLayerRef = {
  subjectType: "account_device",
  subjectId: -1,
};

/**
 * The effective document the dictionary sees: the layers without a design,
 * merged as master merges them (a locked field keeps its lock). Without the
 * layers (not read yet) master's effective document is all there is.
 */
export function dictionaryEffectiveDocument(
  effective: unknown,
  layers: readonly AccountConfigLayer[] | null | undefined,
): unknown {
  if (!layers) return effective;
  return accountConfigLayerView(dictionaryLayers(layers), END_OF_CHAIN).inherited;
}

/** One entry of a language map, as the tabs list it. */
export interface DictionaryMapEntry<V> {
  lang: DictionaryLanguage;
  key: string;
  value: V;
  source: DictionaryEntrySource;
}

/** The layer an inherited entry (an object of leaves) comes from: the one of its first leaf. */
function sourceBelow(
  view: ReturnType<typeof accountConfigLayerView>,
  path: readonly string[],
): { subjectType: string } | undefined {
  const exact = accountConfigLayerSource(view, path);
  if (exact) return exact;
  const prefix = `${path.join(".")}.`;
  const leaf = Object.keys(view.sources).find((known) => known.startsWith(prefix));
  return leaf ? view.sources[leaf] : undefined;
}

function sourceOf(subjectType: string | undefined): DictionaryEntrySource {
  if (subjectType === "company") return "company";
  if (subjectType === "tenant") return "tenant";
  return "inherited";
}

/** The language maps of a document's field, `{}` where there is none. */
function languageMaps(
  document: unknown,
  field: string,
): Partial<Record<DictionaryLanguage, JsonObject>> {
  const map = isJsonObject(document) ? document[field] : undefined;
  if (!isJsonObject(map)) return {};
  const out: Partial<Record<DictionaryLanguage, JsonObject>> = {};
  for (const lang of DICTIONARY_LANGUAGES) {
    const inner = map[lang];
    if (isJsonObject(inner)) out[lang] = inner;
  }
  return out;
}

/**
 * The entries of `field` in the effective document, by language and key, each
 * with the layer it comes from: `own` when the session's layer (`target`)
 * holds the key, or no layer before it does; else the nearest layer before
 * that brings it. A key both hold is the own one (its value wins and stays
 * editable). Without the layers every entry is `own`. `read` turns a stored
 * value into an entry's value, or undefined for a value that is no entry.
 */
export function dictionaryMapEntries<V>(
  effective: unknown,
  field: string,
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
  read: (value: unknown) => V | undefined,
): DictionaryMapEntry<V>[] {
  const maps = languageMaps(dictionaryEffectiveDocument(effective, layers), field);
  const view =
    layers && target
      ? accountConfigLayerView(dictionaryLayers(layers), {
          subjectType: target.subjectType,
          subjectId: target.subjectId,
        })
      : null;
  const out: DictionaryMapEntry<V>[] = [];
  for (const lang of DICTIONARY_LANGUAGES) {
    const map = maps[lang];
    if (!map) continue;
    const before = view ? languageMaps(view.inherited, field)[lang] : undefined;
    const own = view ? languageMaps(view.own, field)[lang] : undefined;
    for (const [key, stored] of Object.entries(map)) {
      const value = read(stored);
      if (value === undefined) continue;
      let source: DictionaryEntrySource = "own";
      if (view && before && key in before && !(own && key in own)) {
        source = sourceOf(sourceBelow(view, [field, lang, key])?.subjectType);
      }
      out.push({ lang, key, value, source });
    }
  }
  return out.sort(
    (a, b) =>
      a.lang.localeCompare(b.lang) || a.key.localeCompare(b.key, "de"),
  );
}

/** Whether a tenant or company locked `field` for every later layer. */
export function dictionaryFieldLocked(
  enforced: readonly EnforcedField[],
  field: string,
): boolean {
  return enforced.some((entry) => entry.field === field);
}

/** The layer's own document of a key (`{}` while the layers are not known). */
export function dictionaryOwnDocument(
  layers: readonly AccountConfigLayer[] | null | undefined,
  target: AccountConfigTarget | null | undefined,
): JsonObject {
  if (!layers || !target) return {};
  return accountConfigLayerView(layers, {
    subjectType: target.subjectType,
    subjectId: target.subjectId,
  }).own;
}

/** How full a layer's document of a key is: its bytes against master's limit. */
export interface DictionaryFill {
  bytes: number;
  limit: number;
  /** 0..1 and beyond; 1 is the limit. */
  ratio: number;
  /** At or past `DICTIONARY_FILL_WARN` of the limit. */
  nearLimit: boolean;
}

/** The share of the limit from which a page warns. */
export const DICTIONARY_FILL_WARN = 0.8;

export function dictionaryFill(ownDocument: unknown): DictionaryFill {
  const bytes = dictionaryDocumentBytes(ownDocument);
  const ratio = bytes / DICTIONARY_DOCUMENT_MAX_BYTES;
  return {
    bytes,
    limit: DICTIONARY_DOCUMENT_MAX_BYTES,
    ratio,
    nearLimit: ratio >= DICTIONARY_FILL_WARN,
  };
}

/** The patch's `{ field: { lang: { key: value } } }` shape, with `rename` removing the old key. */
export function dictionaryEntryPatch(
  field: string,
  lang: DictionaryLanguage,
  key: string,
  value: unknown,
  renamedFrom?: string,
): JsonObject {
  const entries: JsonObject = {};
  if (renamedFrom !== undefined && renamedFrom !== key) entries[renamedFrom] = null;
  entries[key] = value;
  return { [field]: { [lang]: entries } };
}
